"use client"

import { useState, useEffect, useRef } from "react"
import API from "../utils/api"
import "./StockOutflow.css"
import { format } from "date-fns"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import { useNavigate } from "react-router-dom"

const StockOutflow = () => {
  const [allProducts, setAllProducts] = useState([])
  const [products, setProducts] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [currentCustomerName, setCurrentCustomerName] = useState("")
  const [currentDate, setCurrentDate] = useState("")
  const [currentNotes, setCurrentNotes] = useState("")
  const [message, setMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState({
    products: false,
    allProducts: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  // Removed sale-by-date search per new requirements
  const [showSearchResults, setShowSearchResults] = useState(false)
  const searchRef = useRef(null)
  const navigate = useNavigate()

  const addProductToCart = (product, quantity) => {
    const existingIndex = selectedProducts.findIndex((p) => p._id === product._id)

    if (existingIndex >= 0) {
      const updatedProducts = [...selectedProducts]
      updatedProducts[existingIndex].saleQty = quantity
      setSelectedProducts(updatedProducts)
      setMessage(`✅ Updated quantity for ${product.companyName} - ${product.modelNo}`)
    } else {
      const newProduct = {
        ...product,
        // Keep original stock snapshot and separate sale quantity
        stockQty: Number(product.quantity) || 0,
        saleQty: quantity,
        customerName: currentCustomerName,
        date: currentDate,
        notes: currentNotes,
      }
      setSelectedProducts([...selectedProducts, newProduct])
      setMessage(`✅ Added ${product.companyName} - ${product.modelNo} to cart`)
    }
    setShowSearchResults(false)
  }

  const removeProductFromCart = (productId) => {
    setSelectedProducts(selectedProducts.filter((p) => p._id !== productId))
    setMessage("🗑️ Product removed from cart")
  }

  const getTotalItemsInCart = () => {
    return selectedProducts.reduce((total, product) => total + (Number(product.saleQty) || 0), 0)
  }

  // date search removed

  useEffect(() => {
    fetchAllProducts()
    
    // Add click outside listener to close search results
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const fetchAllProducts = async () => {
    setIsLoading((prev) => ({ ...prev, allProducts: true }))
    try {
      const res = await API.get("/products")
      setAllProducts(res.data)
    } catch (err) {
      console.error("Error fetching all products:", err)
    } finally {
      setIsLoading((prev) => ({ ...prev, allProducts: false }))
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch()
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setProducts([])
      setShowSearchResults(false)
      return
    }

    setIsSearching(true)
    try {
      const filtered = allProducts.filter((product) => {
        return (
          product.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.modelNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.invoiceNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.size?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.color?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })

      setProducts(filtered)
      setShowSearchResults(true)
    } catch (err) {
      console.error("Error searching products:", err)
      setMessage("❌ Error searching products")
      setProducts([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleBatchReduceStock = async () => {
    if (selectedProducts.length === 0) {
      setMessage("⚠️ Please add products to cart first.")
      return
    }

    if (!currentCustomerName.trim()) {
      setMessage("⚠️ Please enter customer name.")
      return
    }

    for (const product of selectedProducts) {
      if (!Number.isFinite(Number(product.saleQty)) || Number(product.saleQty) <= 0) {
        setMessage(`⚠️ Invalid quantity for ${product.companyName} - ${product.modelNo}`)
        return
      }
    }

    setIsSubmitting(true)
    setMessage("🔄 Processing batch sale...")

    try {
      const salePromises = selectedProducts.map((product) =>
        API.put(`/products/${product._id}/reduce-stock`, {
          reduceBy: Number(product.saleQty) || 0,
          date: currentDate || new Date().toISOString(),
          partyName: currentCustomerName,
          notes: currentNotes || "",
        }),
      )

      const results = await Promise.all(salePromises)

      setMessage(`✅ Successfully processed ${selectedProducts.length} products for ${currentCustomerName}`)

      setSelectedProducts([])
      setCurrentCustomerName("")
      setCurrentDate("")
      setCurrentNotes("")
      setSearchQuery("")
      setProducts([])
      setShowSearchResults(false)

      fetchAllProducts()
    } catch (err) {
      console.error("Batch sale error:", err)
      const errorMsg = err.response?.data?.message || err.message || "Error processing batch sale"
      setMessage(`❌ ${errorMsg}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const viewProductDetails = (productId) => {
    navigate(`/product-details/${productId}`)
  }

  const navigateToSalesReports = () => {
    navigate("/sales-reports")
  }

  // Add this function to handle product click from date search
  const handleDateSearchProductClick = (productId) => {
    if (productId) {
      viewProductDetails(productId);
    }
  };

  if (isLoading.allProducts) {
    return (
      <div className="stock-outflow-container">
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="stock-outflow-container">
      <div className="page-header">
        <h2>📦 Sales Management System</h2>
        <button onClick={navigateToSalesReports} className="reports-btn">
          📊 View Sales Reports
        </button>
      </div>

      <div className="sales-entry-section">
        <h3>🛒 Multi-Product Sale Entry</h3>

        <div className="customer-info-section large-entry">
          <h4>👤 Customer Information</h4>
          <div className="customer-form">
            <input
              type="text"
              placeholder="Customer/Party Name *"
              value={currentCustomerName}
              onChange={(e) => setCurrentCustomerName(e.target.value)}
              className="customer-input"
              required
            />
            <input
              type="date"
              value={currentDate}
              onChange={(e) => setCurrentDate(e.target.value)}
              max={format(new Date(), "yyyy-MM-dd")}
              className="date-input"
            />
            <input
              type="text"
              placeholder="Notes (optional)"
              value={currentNotes}
              onChange={(e) => setCurrentNotes(e.target.value)}
              className="notes-input"
            />
          </div>
        </div>

        {selectedProducts.length > 0 && (
          <div className="shopping-cart">
            <h4>
              🛒 Shopping Cart ({selectedProducts.length} items, {getTotalItemsInCart()} total qty)
            </h4>
            <div className="cart-items">
              {selectedProducts.map((product) => (
                <div key={product._id} className="cart-item">
                  <div className="cart-item-info">
                    <strong>{product.companyName}</strong> - {product.modelNo}
                    <span className="cart-item-details">
                      Qty: {product.saleQty} | Stock: {Number(product.stockQty ?? product.quantity) || 0} available
                    </span>
                  </div>
                  <button onClick={() => removeProductFromCart(product._id)} className="remove-btn">
                    ❌ Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="cart-actions">
              <button
                onClick={handleBatchReduceStock}
                disabled={isSubmitting || !currentCustomerName.trim()}
                className="process-cart-btn"
              >
                {isSubmitting ? "Processing..." : `Process Sale for ${currentCustomerName || "Customer"}`}
              </button>
              <button onClick={() => setSelectedProducts([])} className="clear-cart-btn">
                🗑️ Clear Cart
              </button>
            </div>
          </div>
        )}

        <div className="search-container" ref={searchRef}>
          <h4>🔍 Search & Add Products</h4>
          <div className="search-bar-wrapper">
            <input
              type="text"
              placeholder="Search by company, model, invoice no, size or color..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setShowSearchResults(true)
              }}
              onFocus={() => setShowSearchResults(true)}
              className="search-input"
            />
            <button className="search-button" onClick={handleSearch} disabled={isSearching}>
              {isSearching ? "Searching..." : "Search"}
            </button>
          </div>

          {showSearchResults && products.length > 0 && (
            <div className="search-results-dropdown">
              <div className="search-results-header">
                <span>📋 Found {products.length} products</span>
                <button 
                  className="close-results-btn"
                  onClick={() => setShowSearchResults(false)}
                >
                  ✕
                </button>
              </div>
              <div className="search-results-list">
                {products.map((p) => {
                  const isExactMatch =
                    p.companyName?.toLowerCase() === searchQuery.toLowerCase() ||
                    p.modelNo?.toLowerCase() === searchQuery.toLowerCase()

                  const isInCart = selectedProducts.some((sp) => sp._id === p._id)

                  return (
                    <div
                      key={p._id}
                      className={`search-result-item ${isInCart ? "in-cart" : ""} ${isExactMatch ? "exact-match" : ""}`}
                    >
                      <div className="search-result-info">
                        <div className="search-result-main">
                          <strong>{p.companyName}</strong> - {p.modelNo}
                          {p.invoiceNo && <span className="invoice-no"> (Invoice: {p.invoiceNo})</span>}
                        </div>
                        <div className="search-result-details">
                          {p.size && <span>Size: {p.size}</span>}
                          {p.color && <span>Color: {p.color}</span>}
                          <span className="product-stock">Stock: {p.quantity} pcs</span>
                          <span className="product-alert">Alert Qty: {p.alertQty}</span>
                        </div>
                        {isExactMatch && <span className="exact-match-tag">Exact Match</span>}
                        {isInCart && <span className="in-cart-tag">In Cart</span>}
                      </div>
                      <div className="search-result-actions">
                        <input
                          type="number"
                          min="1"
                          placeholder="Qty"
                          className="qty-input"
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              const qty = Number.parseInt(e.target.value)
                              if (qty > 0) {
                                addProductToCart(p, qty)
                                e.target.value = ""
                              }
                            }
                          }}
                        />
                        <button
                          onClick={(e) => {
                            const container = e.currentTarget.closest(".search-result-actions") || e.currentTarget.parentElement
                            const qtyInput = container ? container.querySelector(".qty-input") : null
                            const qty = Number.parseInt(qtyInput?.value)
                            if (Number.isFinite(qty) && qty > 0) {
                              addProductToCart(p, qty)
                              if (qtyInput) qtyInput.value = ""
                            } else {
                              setMessage("⚠️ Please enter valid quantity")
                            }
                          }}
                          className="add-to-cart-btn"
                        >
                          {isInCart ? "Update" : "Add to Cart"}
                        </button>
                        <button onClick={() => viewProductDetails(p._id)} className="view-details-btn">
                          👁️
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {showSearchResults && searchQuery && products.length === 0 && !isSearching && (
            <div className="search-results-dropdown">
              <div className="no-search-results">
                <p>No products found for "{searchQuery}"</p>
              </div>
            </div>
          )}
        </div>

        {message && (
          <p
            className={`message ${message.startsWith("❌") ? "error" : message.startsWith("⚠️") ? "warning" : "success"}`}
          >
            {message}
          </p>
        )}
      </div>

      {/* Removed sale by date search block */}
    </div>
  )
}

export default StockOutflow
